import {UserComponentUsageReificationError} from './errors';
import {
  type UserComponentUsage,
  type UserComponentUsageAncestorFlags,
} from './types';
import {Plugins, Component} from '@packages/systems/wfdl';
import {Tree} from '@packages/systems/tree';
import {pageComponentType} from '@packages/utilities/platform';
import {jsonNodesToUserComponentUsages} from './jsonNodesToUserComponentUsages';
import {singleMemo} from '@packages/utilities/memo';
import {namesEqual, getName} from '@packages/systems/wfdl-Qualified';
import {dataTypesFromComponents} from '@packages/systems/core/render';
import {emptyArray} from '@packages/utilities/fp/utils';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type _UserComponentUsageMap = {
  [pageId: string]: UserComponentUsage[];
};

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type UserComponentUsageMap = _UserComponentUsageMap | null;

export function reifyUserComponentUsageMap(
  rawUsageMap:
    | {
        [key: string]: unknown;
      }
    | undefined
): UserComponentUsageMap {
  if (rawUsageMap === undefined) {
    // undefined value means that the beta flag is not turned on
    return emptyUserComponentUsageMap();
  }

  if (!isPlainObject(rawUsageMap)) {
    throw new UserComponentUsageReificationError(
      'Received usage map is not an object'
    );
  }

  const usageMap: _UserComponentUsageMap = {};

  for (const pageId in rawUsageMap) {
    if (!isNonEmptyString(pageId)) {
      throw new UserComponentUsageReificationError(
        'Invalid key in received usage map'
      );
    }

    const rawUsages = rawUsageMap[pageId];

    if (!Array.isArray(rawUsages)) {
      throw new UserComponentUsageReificationError(
        'Invalid value in received usage map'
      );
    }

    usageMap[pageId] = reifyUserComponentUsages(rawUsages);
  }

  return usageMap;
}

export function emptyUserComponentUsageMap(): UserComponentUsageMap {
  return null;
}

function reifyUserComponentUsages(
  rawUsages: ReadonlyArray<unknown>
): UserComponentUsage[] {
  return rawUsages.map((rawUsage) => {
    if (!isPlainObject(rawUsage)) {
      throw new UserComponentUsageReificationError(
        'Invalid usage shape in received usage map'
      );
    }

    const {
      instanceId,
      rootId,
      parentRootId,
      ancestorFlags: rawAncestorFlags,
    } = rawUsage as {
      instanceId: string;
      rootId: string;
      parentRootId: string | null;
      ancestorFlags: Record<string, boolean>;
    };

    if (!isNonEmptyString(instanceId)) {
      throw new UserComponentUsageReificationError(
        'Invalid instanceId in received usage map'
      );
    }

    if (!isNonEmptyString(rootId)) {
      throw new UserComponentUsageReificationError(
        'Invalid rootId in received usage map'
      );
    }

    if (parentRootId !== null && !isNonEmptyString(parentRootId)) {
      throw new UserComponentUsageReificationError(
        'Invalid parentRootId in received usage map'
      );
    }

    if (!isPlainObject(rawAncestorFlags)) {
      throw new UserComponentUsageReificationError(
        'Invalid ancestorFlags shape in received usage map'
      );
    }

    const ancestorFlags: UserComponentUsageAncestorFlags = {};

    for (const elementType in rawAncestorFlags) {
      if (!isNonEmptyString(elementType)) {
        throw new UserComponentUsageReificationError(
          'Invalid ancestorFlags key in received usage map'
        );
      }

      if (rawAncestorFlags[elementType] !== true) {
        throw new UserComponentUsageReificationError(
          'Invalid ancestorFlags value in received usage map'
        );
      }

      ancestorFlags[elementType] = true;
    }

    return {
      instanceId,
      rootId,
      parentRootId,
      ancestorFlags,
    };
  });
}

function isPlainObject(data: unknown): boolean {
  return data !== null && typeof data === 'object' && !Array.isArray(data);
}

function isNonEmptyString(val: unknown): boolean {
  return Boolean(val) && typeof val === 'string';
}

export function detectConstrainingUserComponents({
  currentPageId,
  currentComponentType,
  componentMap,
  plugins,
  userComponentUsageMap,
}: {
  currentPageId: string;
  currentComponentType: Plugins.QualifiedElement;
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  userComponentUsageMap: UserComponentUsageMap;
}): UserComponentUsage[] {
  return memoizedDetectConstrainingUserComponents(currentPageId)(
    currentComponentType
  )(componentMap, plugins, userComponentUsageMap);
}

const memoizedDetectConstrainingUserComponents = singleMemo(
  (currentPageId: string) =>
    singleMemo((currentComponentType: Plugins.QualifiedElement) => {
      // We use `memoizedResult` because we want to derive the ConstrainingUserComponents only
      // once after a User Component focuses/unfocuses. The consecutive calls while
      // the same User Component is focused can return the same value because nothing
      // relevant to the ConstrainingUserComponents can change in `componentMap`, `plugins`,
      // or `userComponentMap` as long as the same User Component instance is focused.
      let memoizedResult: UserComponentUsage[] | null = null;

      return (
        componentMap: Component.ComponentMap,
        plugins: Plugins.PluginsType,
        userComponentUsageMap: UserComponentUsageMap
      ): UserComponentUsage[] => {
        if (memoizedResult) {
          return memoizedResult;
        }

        if (namesEqual(currentComponentType, pageComponentType)) {
          memoizedResult = emptyArray;
          return memoizedResult;
        }

        const tree = Tree.create(componentMap);

        const {pageNodes, symbolNodes} = tree.toJSONNodes(plugins);

        const dataTypes = dataTypesFromComponents(plugins, componentMap);

        const usagesOnCurrentPage = jsonNodesToUserComponentUsages(
          dataTypes,
          pageNodes
        );

        const usagesInAllSymbols = jsonNodesToUserComponentUsages(
          dataTypes,
          symbolNodes
        );

        const usagesOnOtherPages = getUsagesOnOtherPages(
          userComponentUsageMap,
          currentPageId
        );

        const rootUsageMap = groupUsagesByRootId(
          usagesOnCurrentPage,
          usagesInAllSymbols,
          usagesOnOtherPages
        );

        const userComponentRootId = getName(currentComponentType);

        memoizedResult = findConstrainingUserComponentUsages(
          rootUsageMap,
          userComponentRootId
        );
        return memoizedResult;
      };
    })
);

function getUsagesOnOtherPages(
  userComponentUsageMap: UserComponentUsageMap,
  currentPageId: string
): UserComponentUsage[] {
  const usagesOnOtherPages: UserComponentUsage[] = [];
  for (const usagePageId in userComponentUsageMap) {
    if (usagePageId === currentPageId) {
      continue;
    }
    Array.prototype.push.apply(
      usagesOnOtherPages,
      // @ts-expect-error - TS2345 - Argument of type 'UserComponentUsage[] | undefined' is not assignable to parameter of type 'any[]'.
      userComponentUsageMap[usagePageId]
    );
  }
  return usagesOnOtherPages;
}

function groupUsagesByRootId(
  usagesOnCurrentPage: UserComponentUsage[],
  usagesInAllSymbols: UserComponentUsage[],
  usagesOnOtherPages: UserComponentUsage[]
): RootUsageMap {
  const rootUsageMap: RootUsageMap = {};

  expandRootUsageMap(rootUsageMap, usagesOnCurrentPage);
  expandRootUsageMap(rootUsageMap, usagesInAllSymbols);
  expandRootUsageMap(rootUsageMap, usagesOnOtherPages);

  return rootUsageMap;
}

function expandRootUsageMap(
  rootUsageMap: RootUsageMap,
  userComponentUsages: UserComponentUsage[]
): void {
  for (const userComponentUsage of userComponentUsages) {
    const {rootId} = userComponentUsage;
    if (!rootUsageMap[rootId]) {
      rootUsageMap[rootId] = [];
    }
    rootUsageMap[rootId].push(userComponentUsage);
  }
}

type RootUsageMap = {
  [rootId: string]: UserComponentUsage[];
};

function findConstrainingUserComponentUsages(
  rootUsageMap: RootUsageMap,
  userComponentRootId: string
): UserComponentUsage[] {
  const constrainingUsages: UserComponentUsage[] = [];

  const visitedRootIds: {
    [id: string]: true;
  } = {};
  const rootIdStack: string[] = [userComponentRootId];

  while (rootIdStack.length > 0) {
    const rootId = rootIdStack.pop();
    if (!rootId) continue;
    const instances = rootUsageMap[rootId];

    if (visitedRootIds[rootId] || !instances) {
      continue;
    }
    visitedRootIds[rootId] = true;

    for (const instance of instances) {
      const {parentRootId, ancestorFlags} = instance;

      if (hasAncestorFlag(ancestorFlags)) {
        constrainingUsages.push(instance);
      }

      if (parentRootId) {
        rootIdStack.push(parentRootId);
      }
    }
  }

  return constrainingUsages;
}

function hasAncestorFlag(
  ancestorFlags: UserComponentUsageAncestorFlags
): boolean {
  for (const ancestorFlag in ancestorFlags) {
    return true;
  }
  return false;
}
